import { useId, useState } from "react";
import {
  DRAFT_EDIT_FIELDS,
  DRAFT_FIELD_LIMITS,
  editProblem,
  editValue,
  editValuesEmpty,
  editValuesValid,
  withEditValue,
  type DraftEditMode,
  type DraftEditValues,
} from "@/lib/formulator/draft-text";
import { cn } from "@/lib/utils";

const fieldClass =
  "w-full rounded-[10px] border border-[#3F3F46] bg-[#111113] px-3 py-2.5 text-[13px] leading-[1.55] text-foreground outline-none transition-[border-color,box-shadow] focus:border-[#66A1FF] focus:ring-2 focus:ring-[#66A1FF]/25";

export interface DraftEditorProps {
  /** The text the editor opens with: the stored draft, or the text put together from the answers. */
  initial: DraftEditValues;
  /**
   * Create (a new item: the main boxes are required) or improve (adding to an
   * existing item: every box may be empty, an empty box keeps the item's text,
   * and at least one box needs text). Defaults to create.
   */
  mode?: DraftEditMode;
  /** True while the edit is being saved: every box and button is locked. */
  saving: boolean;
  /** Called with the edited text when the person chooses Save; nothing is saved before. */
  onSave: (values: DraftEditValues) => void;
  /** Leave the editor; the edit is discarded. */
  onCancel: () => void;
}

/**
 * The review screen's Edit text mode (epic PI-SPEC-SW-E321, feature
 * PI-SPEC-SW-1721): one text box per part of the item, the lists one entry
 * per line, capped at the contract's limits. Plain text only; SpecTree lays
 * it out in ADO. Save hands the values to the caller; Cancel discards them.
 */
export function DraftEditor({ initial, mode = "create", saving, onSave, onCancel }: DraftEditorProps) {
  const [values, setValues] = useState<DraftEditValues>(initial);
  const [tried, setTried] = useState(false);
  const baseId = useId();
  const valid = editValuesValid(values, mode);
  const nothingAdded = mode === "improve" && editValuesEmpty(values);

  return (
    <form
      className="flex flex-col gap-3.5 py-3"
      data-testid="formulator-draft-editor"
      onSubmit={(event) => {
        event.preventDefault();
        setTried(true);
        if (valid && !saving) onSave(values);
      }}
    >
      <p className="text-[12px] text-muted-foreground">
        Plain text only. SpecTree lays it out in ADO: lists become bullets or numbered steps.
        {mode === "improve" && " Only what you add here is written; leave a box empty to keep the item's text as it is."}
      </p>
      {DRAFT_EDIT_FIELDS[values.kind].map(({ field, label }) => {
        const limit = DRAFT_FIELD_LIMITS[field];
        const id = `${baseId}-${field}`;
        const problemId = `${id}-problem`;
        const problem = editProblem(values, field, mode);
        const showProblem = tried && problem !== null;
        const text = editValue(values, field);
        return (
          <div key={field} className="flex flex-col gap-1.5">
            <label htmlFor={id} className="text-[13px] font-medium text-muted-foreground">
              {label}
            </label>
            <textarea
              id={id}
              value={text}
              onChange={(event) => {
                setValues((current) => withEditValue(current, field, event.target.value));
              }}
              disabled={saving}
              maxLength={limit.list ? undefined : limit.max}
              rows={limit.list ? 5 : field === "frequency" ? 1 : 4}
              aria-invalid={showProblem}
              aria-describedby={showProblem ? problemId : undefined}
              className={cn(fieldClass, "resize-y disabled:opacity-60", showProblem && "border-amber-500/60")}
              data-testid="formulator-draft-field"
              data-field={field}
            />
            {showProblem && (
              <span id={problemId} role="alert" className="text-[12px] text-amber-300" data-testid="formulator-draft-field-problem">
                {problem}
              </span>
            )}
          </div>
        );
      })}
      {tried && nothingAdded && (
        <span role="alert" className="text-[12px] text-amber-300" data-testid="formulator-draft-nothing-added">
          Add text to at least one box.
        </span>
      )}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="inline-flex h-[30px] items-center rounded-lg border border-[#27272A] bg-[#1A1A1E] px-[11px] text-[13px] font-medium hover:bg-[#222227] disabled:opacity-40"
          data-testid="formulator-draft-cancel"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className={cn(
            "inline-flex h-[30px] items-center rounded-lg bg-primary px-[13px] text-[13px] font-semibold text-white transition-colors hover:bg-[#C91E38]",
            "disabled:pointer-events-none disabled:opacity-40"
          )}
          data-testid="formulator-draft-save"
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </form>
  );
}
